import React, { useEffect, useMemo, useState, useCallback, useRef} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useFetchDocuments from './../../components/hooks/useFetchDocuments';
import DOMPurify from 'dompurify';
import { useAuth } from './../../context/AuthContext';
import { useFlow } from './../../context/FlowContext';
import visorService from './../../services/VisorService';
import getBaseUrl from './../../utils/baseUrl';
import DownloadButton from './../../components/Buttons/DownloadButton';
import greenTick from './../../assets/images/green-tick.png';
import { marked } from 'marked';

import './../../styles/Main.css';
import './../../styles/AndroidGuide.css';

const fetchGuideHtml = async (fileName) => {
  const res = await fetch(`${getBaseUrl()}/guides/android/${fileName}`);
  if (!res.ok) throw new Error(`Failed to fetch ${fileName}`);
  const text = await res.text();
  return DOMPurify.sanitize(text);
};

export default function AndroidDesktopPage() {
  const { constabulary, androidApk, forcePrefix } = useAuth();
  const {
    pnc,
    setPnc,
    visorId,
    setVisorId,
    suffix,
    setSuffix,
    created,
    setCreated,
    finalVisorId,
    setFinalVisorId
  } = useFlow();

  const [documents, setDocuments] = useState([]);
  const { documentName } = useParams();
  const navigate = useNavigate();
const noopFetchContent = useCallback((_fileName) => {

}, []);
  useFetchDocuments(
    'android',                               
    documentName || 'android-desktop',       
    setDocuments,
    noopFetchContent,
    () => {}                                 
  );

  // ---- Existing Android guide HTML blocks ----
  const [introHtml, setIntroHtml] = useState('');
  const [guideHtml, setGuideHtml] = useState('');
  const [visorHelpHtml, setVisorHelpHtml] = useState('');
  const [over14Html, setOver14Html] = useState('');
  const [under14Html, setUnder14Html] = useState('');
  const [finalChecksHtml, setFinalChecksHtml] = useState('');
  const [docLoading, setDocLoading] = useState(true);
  const [docError, setDocError] = useState('');

  const [pncErr, setPncErr] = useState('');
  const [visorErr, setVisorErr] = useState('');
  const [suffixErr, setSuffixErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const [existingVisors, setExistingVisors] = useState([]);
  const [existingVisorError, setExistingVisorError] = useState('');
  const [content, setContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const contentRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setDocLoading(true);
        const [
          intro,
          guide,
          visorHelp,
          over14,
          under14,
          finalChecks
        ] = await Promise.all([
          fetchGuideHtml('android1.html'),
          fetchGuideHtml('android2.html'),
          fetchGuideHtml('android3.html'),
          fetchGuideHtml('instover14.html'),
          fetchGuideHtml('inunder14.html'),
          fetchGuideHtml('FinalChecks.html')
        ]);
        if (cancelled) return;
        setIntroHtml(intro);
        setGuideHtml(guide);
        setVisorHelpHtml(visorHelp);
        setOver14Html(over14);
        setUnder14Html(under14);
        setFinalChecksHtml(finalChecks);
        setDocError('');
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setDocError('Could not load guide content. Please try again later.');
        }
      } finally {
        if (!cancelled) setDocLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Existing Visor IDs (dropdown)
  useEffect(() => {
    (async () => {
      try {
        const list = await visorService.getVisorIdList();
        setExistingVisors(list || []);
        setExistingVisorError('');
      } catch (err) {
        console.error(err);
        setExistingVisorError('No inactive device IDs found. Please create a new one.');
      }
    })();
  }, []);

  // ---- Handlers (unchanged) ----
  const handlePncChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
    setPnc(value);
    setCreated(false);
  };

  const handleExistingVisorSelect = (e) => {
    const selected = e.target.value;

    if (!selected) {
      setFinalVisorId('');
      setCreated(false);
      return;
    }

    setFinalVisorId(selected);
    setCreated(true);
    setCreateError('');
    // optional: clear manual fields when selecting from dropdown
    setVisorId('');
    setSuffix('');
    setVisorErr('');
    setSuffixErr('');
  };

  const handleVisorChange = (e) => {
    let value = e.target.value
      .replace(/[^a-zA-Z0-9-]/g, '')
      .toUpperCase()
      .slice(0, 13);
    setVisorId(value);
    setCreated(false);

    if (!value) setVisorErr('Device ID is required');
    else if (value.length < 3) setVisorErr('Device ID looks too short');
    else setVisorErr('');
  };

  const handleSuffixChange = (e) => {
    let value = e.target.value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 2);
    setSuffix(value);
    setCreated(false);
    setSuffixErr('');
  };

  const previewVisor = useMemo(() => {
    const parts = [visorId || 'ABC123', suffix || '0'];
    return parts.join('-');
  }, [visorId, suffix]);

  const canCreate = useMemo(() => {
    return (
      !creating &&
      !created &&
      visorId &&
      !pncErr &&
      !visorErr &&
      !suffixErr
    );
  }, [creating, created, visorId, pncErr, visorErr, suffixErr]);

  const createVisor = async () => {
    if (!canCreate) return;
    try {
      setCreating(true);
      setCreateError('');
      const newVisorId = `${visorId}-${suffix}`;
      await visorService.createVisor(newVisorId, 0);
      setFinalVisorId(newVisorId);
      setCreated(true);
    } catch (e) {
      setCreateError(e?.message || 'Failed to create Device ID. Please try again.');
      setCreated(false);
    } finally {
      setCreating(false);
    }
  };
  const addLinkClickListener = () => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach(link => {
        link.removeEventListener('click', handleLinkClick); // Remove previous listeners
        link.addEventListener('click', handleLinkClick);
      });
    }
  };
    const fetchContent = useCallback((fileName) => {
    fetch(`${getBaseUrl()}/guides/mac/${fileName}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        let htmlContent = marked(text, { gfm: true, breaks: true });
        const baseUrl = getBaseUrl();
        htmlContent = htmlContent.replace(/src="(?:\.\/)?images\//gi, `src="${baseUrl}/guides/mac/Images/`);

        const sanitizedContent = DOMPurify.sanitize(htmlContent);
        setContent(sanitizedContent);

        setTimeout(() => addLinkClickListener(), 100);
      })
      .catch((error) => {
        console.error('Error fetching the HTML file:', error);
        setContent('Error loading content. Please try again later.');
      });
  }, []);

  const copyVisor = async () => {
    try {
      await navigator.clipboard.writeText(finalVisorId.trim());
      const btn = document.getElementById('copy-desktop-visor');
      if (!btn) return;
      const original = btn.textContent;
      btn.textContent = 'Copied';
      setTimeout(() => {
        btn.textContent = original;
      }, 1200);
    } catch {
      alert('Copy failed. Please copy manually.');
    }
  };

  const SkeletonBlock = ({ lines = 3 }) => (
    <div className="skeleton">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="skeleton__line" />
      ))}
    </div>
  );

    const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

    const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length > 2) {
      const escapedValue = escapeRegExp(value);
      const regex = new RegExp(`(${escapedValue})`, 'gi');
      const highlightedContent = content.replace(regex, '<span class="highlight">$1</span>');
      setContent(DOMPurify.sanitize(highlightedContent));

      setTimeout(() => {
        const firstHighlight = document.querySelector('.highlight');
        if (firstHighlight) {
          firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      fetchContent(documentName);
    }
  };
  const handleLinkClick = (event) => {
    event.preventDefault();
    const href = event.currentTarget.getAttribute('href');  
    if (href && (href.endsWith('.mp4') || href.endsWith('.mov') || href.endsWith('.webm'))) {
      const videoPath = href.startsWith('/') ? href : `${getBaseUrl()}/${href}`;
    } else if (href && href.startsWith('guides/mac/')) {
      const documentName = href.split('/').pop();
      navigate(`/mac/${documentName}`);
    } else {
      window.location.href = href;
    }
  };
  const activeDoc = documentName || 'android-desktop';

  return (
    <div className="android-desktop-page-layout android-desktop-page">
      



      <div className="">
        <div className="">
        <p className="android-desktop-title"></p>
          <div>    
          <div className="search-input-button-container">
          <div className="search-bar">
            {/* <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
            /> */}
          </div>
          {/* <button className="search-button" title="Clear" onClick={handleSearch}>Clear</button> */}
        </div>       


      {/* <div className="android-desktop-header">
        {constabulary && (
          <p className="android-desktop-sub">{constabulary}</p>
        )}
      </div> */}
      
            {/* --- Intro / general info --- */}
            <section className="android-desktop-card">
            <div className="android-desktop-title-box">
            
              <span className="android-desktop-title-text">Install eSafe for Android</span>
            </div>
              <h2>Before you start</h2>
             

              {docLoading ? (
                <SkeletonBlock lines={4} />
              ) : docError ? (
                <p className="android-desktop-error">{docError}</p>
              ) : (
                <>
                  <div
                    className="html-content"
                    dangerouslySetInnerHTML={{ __html: introHtml }}
                  />
                  <div
                    className="html-content"
                    style={{ marginTop: 16 }}
                    dangerouslySetInnerHTML={{ __html: guideHtml }}
                  />
                </>
              )}
            </section>

            {/* --- Step 1: Device ID --- */}
            <section className="android-desktop-card">
              <h2>Step 1 - Create the Device ID</h2>
              <div className="android-content">
                <div className="android-card android-card--info">
                  <div>
                    
                    <p className="android-desktop-muted">
                      Every installation requires a fresh Device ID. This is the name shown
                      in the console.
                    </p>
                  </div>

                  {!docLoading && visorHelpHtml && (
                    <div
                      className="html-content"
                      dangerouslySetInnerHTML={{ __html: visorHelpHtml }}
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  <div className="android-desktop-form">
                    {/* Existing Device ID dropdown */}
                    <div className="android-desktop-form-row">
                      <h3 htmlFor="existing-visor-desktop">
                        Already created the Device ID?
                      </h3>
                      <select
                        id="existing-visor-desktop"
                        className="android-input"
                        value={finalVisorId || ''}
                        onChange={handleExistingVisorSelect}
                      >
                        <option value="">If yes, click here to select it</option>
                        {existingVisors.map((v) => (
                          <option key={v.id} value={v.username}>
                            {v.username}
                          </option>
                        ))}
                      </select>
                      {existingVisorError && (
                        <div className="field-error" role="alert">
                          {existingVisorError}
                        </div>
                      )}
                    </div>

                    <div className="android-desktop-form-grid">
                      <div className="android-desktop-form-col">
                        <label htmlFor="visor-desktop">
                          Device ID <span className="req">*</span>
                        </label>
                        <input
                          id="visor-desktop"
                          value={visorId || finalVisorId}
                          onChange={handleVisorChange}
                          placeholder="e.g. ABC123"
                        />
                        {visorErr && (
                          <div className="field-error" role="alert">
                            {visorErr}
                          </div>
                        )}
                      </div>

                      <div className="android-desktop-form-col android-desktop-form-col--tiny">
                        <label htmlFor="suffix-desktop">Suffix</label>
                        <input
                          id="suffix-desktop"
                          value={suffix}
                          maxLength={2}
                          onChange={handleSuffixChange}
                          placeholder="0"
                        />
                        {suffixErr && (
                          <div className="field-error" role="alert">
                            {suffixErr}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="preview">
                      <div className="preview__label">Your Device ID will be:</div>
                      <div className="preview__value" aria-live="polite">
                        {finalVisorId ? finalVisorId : previewVisor}
                      </div>
                    </div>

                    <button
                      className="btn btn--primary"
                      onClick={createVisor}
                      disabled={!canCreate}
                    >
                      {creating ? (
                        'Creating…'
                      ) : created ? (
                        <>
                          <img src={greenTick} alt="" className="icon-inline" />
                          Created
                        </>
                      ) : (
                        'Create Device ID'
                      )}
                    </button>

                    {createError && (
                      <div
                        className="field-error"
                        role="alert"
                        style={{ marginTop: 8 }}
                      >
                        {createError}
                      </div>
                    )}

                    {created && (
                      <div className="success" style={{ marginTop: 16 }}>
                        <div className="success__body">
                          <div className="success__title">Device ID created</div>
                          <div className="success__row">
                            <div className="success__id">{finalVisorId}</div>
                            <button
                              id="copy-desktop-visor"
                              type="button"
                              className="btn btn--secondary success__copy"
                              onClick={copyVisor}
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* --- Step 2: Installation (version split) --- */}
            <section className="android-desktop-card">
                  <h2>Step 2 - Install the eSafe app</h2>

              <div className="android-content">
                <div className="android-card android-card--info">
                  <p className="android-desktop-muted">
                    Follow the instructions that match the Android version on the device.
                  </p>
                  <div className="android-desktop-columns">
                    <div className="android-desktop-column">
                      <h3>Android 14 and above</h3>
                      {docLoading ? (
                        <SkeletonBlock lines={4} />
                      ) : (
                        <div
                          className="html-content"
                          dangerouslySetInnerHTML={{ __html: over14Html }}
                        />
                      )}
                    </div>
                    <div className="android-desktop-column">
                      <h3>Android 13 and below</h3>
                      {docLoading ? (
                        <SkeletonBlock lines={4} />
                      ) : (
                        <div
                          className="html-content"
                          dangerouslySetInnerHTML={{ __html: under14Html }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* --- Step 3: Final checks + download --- */}
            <section className="android-desktop-card">
                  <h2>Step 3 - Final checks & download</h2>

              <div className="android-content">
                <div className="android-card android-card--info">
                  {docLoading ? (
                    <SkeletonBlock lines={4} />
                  ) : (
                    <div
                      className="html-content"
                      dangerouslySetInnerHTML={{ __html: finalChecksHtml }}
                    />
                  )}

                  <div className="android-desktop-download">
                    <DownloadButton
                      className="btn-apk-desktop"
                      link={androidApk}
                      title="Download eSafe APK"
                    />
                    {!created && (
                      <div
                        className="field-error"
                        role="alert"
                        style={{ marginTop: 8 }}
                      >
                        Create the Device ID first so it’s ready during activation.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
